import { useEffect, useState } from "react";
import { ArrowUpRight, Lock } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useSite } from "../context/SiteContext";
import { Reveal } from "../components/Reveal";

function MentoriaCard({ item, index, initialCount, onOpen }) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [comments, setComments] = useState([]);
  const [text, setText] = useState("");
  const [total, setTotal] = useState(initialCount || 0);
  const [loadingComments, setLoadingComments] = useState(false);
  const [sending, setSending] = useState(false);

  const toggle = async () => {
    const next = !open;
    setOpen(next);
    if (next) {
      setLoadingComments(true);
      try {
        const { data } = await api.get(`/mentoria/${item.id}/comments`);
        setComments(data);
        setTotal(data.length);
      } catch {
        toast.error("Não foi possível carregar os comentários.");
      } finally {
        setLoadingComments(false);
      }
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    setSending(true);
    try {
      const { data } = await api.post(`/mentoria/${item.id}/comments`, { text });
      setComments([...comments, data]);
      setTotal(total + 1);
      setText("");
    } catch {
      toast.error("Não foi possível comentar.");
    } finally {
      setSending(false);
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/comments/${id}`);
      setComments(comments.filter((c) => c.id !== id));
      setTotal(total - 1);
    } catch {
      toast.error("Não foi possível excluir.");
    }
  };

  return (
    <Reveal delay={index * 0.1}>
      <div className="flex h-full flex-col border border-gold/15 bg-[#0a0a0a] transition-colors duration-500 hover:border-gold/40" data-testid={`mentoria-card-${index}`}>
        <button onClick={() => onOpen(item)} className="group flex flex-1 flex-col p-8 text-left" data-testid={`mentoria-open-${index}`}>
          <span className="inline-flex w-fit border border-gold/30 px-3 py-1 text-[0.65rem] font-semibold uppercase tracking-[0.2em] text-gold" data-testid={`mentoria-tag-${index}`}>
            {item.tag}
          </span>
          <h2 className="mt-6 font-serif text-2xl leading-snug text-zinc-100">{item.title}</h2>
          <p className="mt-4 flex-1 text-sm leading-relaxed text-zinc-400">{item.description}</p>
          <span className="mt-8 flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-gold">
            Acessar <ArrowUpRight size={14} className="transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
          </span>
        </button>
        <div className="border-t border-gold/10 px-8 py-4">
          <button onClick={toggle} className="text-[0.65rem] font-semibold uppercase tracking-[0.2em] text-zinc-500 transition-colors hover:text-gold" data-testid={`comments-toggle-${index}`}>
            Mural ({total}) {open ? "▲" : "▼"}
          </button>
          {open && (
            <div className="mt-5" data-testid={`comments-section-${index}`}>
              {loadingComments ? (
                <p className="text-xs text-zinc-600">Carregando…</p>
              ) : (
                <div className="flex max-h-56 flex-col gap-4 overflow-y-auto pr-1">
                  {comments.length === 0 && (
                    <p className="text-xs text-zinc-600">Seja o primeiro a comentar.</p>
                  )}
                  {comments.map((c) => (
                    <div key={c.id} className="border-l border-gold/25 pl-4" data-testid={`comment-${c.id}`}>
                      <div className="flex items-center justify-between gap-3">
                        <div className="flex items-center gap-2.5">
                          {c.user_avatar ? (
                            <img src={c.user_avatar} alt="" className="h-6 w-6 rounded-full border border-gold/30 object-cover" />
                          ) : (
                            <span className="flex h-6 w-6 items-center justify-center rounded-full border border-gold/30 font-serif text-[0.6rem] text-gold">
                              {c.user_name?.[0]?.toUpperCase()}
                            </span>
                          )}
                          <p className="text-[0.65rem] font-semibold uppercase tracking-[0.15em] text-gold/80">{c.user_name}</p>
                        </div>
                        {(c.user_id === user?.id || user?.role === "admin") && (
                          <button onClick={() => remove(c.id)} className="text-[0.6rem] uppercase tracking-[0.15em] text-zinc-600 hover:text-red-400" data-testid={`comment-delete-${c.id}`}>
                            Excluir
                          </button>
                        )}
                      </div>
                      <p className="mt-1 text-sm leading-relaxed text-zinc-300">{c.text}</p>
                      <button
                        onClick={async () => {
                          try {
                            const { data } = await api.post(`/comments/${c.id}/react`);
                            setComments(comments.map((x) =>
                              x.id === c.id
                                ? { ...x, reactions: data.reacted ? [...(x.reactions || []), user.id] : (x.reactions || []).filter((r) => r !== user.id) }
                                : x
                            ));
                          } catch {
                            toast.error("Não foi possível reagir.");
                          }
                        }}
                        className={`mt-2 flex items-center gap-1.5 text-xs transition-all hover:scale-110 ${(c.reactions || []).includes(user?.id) ? "text-gold" : "text-zinc-600 hover:text-gold/70"}`}
                        data-testid={`comment-react-${c.id}`}
                        aria-label="Reagir"
                      >
                        ✦ {(c.reactions || []).length > 0 && <span>{(c.reactions || []).length}</span>}
                      </button>
                    </div>
                  ))}
                </div>
              )}
              <form onSubmit={submit} className="mt-5 flex gap-2" data-testid={`comment-form-${index}`}>
                <input
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  maxLength={500}
                  placeholder="Deixe sua reflexão…"
                  className="input-dark flex-1 !py-2.5 text-xs"
                  data-testid={`comment-input-${index}`}
                />
                <button type="submit" disabled={sending} className="btn-gold !px-4 !py-2.5" data-testid={`comment-submit-${index}`}>
                  {sending ? "…" : "Enviar"}
                </button>
              </form>
            </div>
          )}
        </div>
      </div>
    </Reveal>
  );
}

export default function Comunidade() {
  const { user, setUser } = useAuth();
  const { site } = useSite();
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([api.get("/mentoria"), api.get("/mentoria-comments-counts")])
      .then(([a, b]) => {
        setItems(a.data);
        setCounts(b.data);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const open = (item) => {
    if (!item.url || item.url === "#") {
      toast.info("Conteúdo em preparação. Em breve disponível para membros.");
      return;
    }
    window.open(item.url, "_blank");
  };

  return (
    <div className="mx-auto max-w-7xl px-6 py-20 lg:px-12 lg:py-32" data-testid="comunidade-page">
      <Reveal>
        <div className="mb-12 flex items-center gap-5" data-testid="profile-avatar-section">
          {user?.avatar_url ? (
            <img src={user.avatar_url} alt="Sua foto de perfil" className="h-16 w-16 rounded-full border border-gold/40 object-cover" data-testid="profile-avatar-image" />
          ) : (
            <span className="flex h-16 w-16 items-center justify-center rounded-full border border-gold/40 font-serif text-xl text-gold" data-testid="profile-avatar-placeholder">
              {user?.name?.[0]?.toUpperCase() || "?"}
            </span>
          )}
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-gold">Sua foto de perfil</p>
            <label className="mt-2 inline-block cursor-pointer text-xs text-zinc-400 underline decoration-gold/40 underline-offset-4 transition-colors hover:text-gold" data-testid="avatar-upload-button">
              {user?.avatar_url ? "Trocar foto" : "Enviar foto"}
              <input
                type="file"
                accept="image/*"
                className="hidden"
                onChange={async (e) => {
                  const file = e.target.files?.[0];
                  if (!file) return;
                  const fd = new FormData();
                  fd.append("file", file);
                  try {
                    const { data } = await api.post("/profile/avatar", fd);
                    setUser({ ...user, avatar_url: data.avatar_url });
                    toast.success("Foto de perfil atualizada.");
                  } catch {
                    toast.error("Não foi possível enviar a foto.");
                  }
                  e.target.value = "";
                }}
              />
            </label>
          </div>
        </div>
        <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.25em] text-gold">
          <Lock size={12} /> Área de membros
        </p>
        <h1 className="mt-6 font-serif text-5xl text-zinc-100 sm:text-6xl" data-testid="community-welcome">
          Bem-vindo(a), {user?.name?.split(" ")[0]}.
        </h1>
        <p className="mt-6 max-w-xl text-base leading-relaxed text-zinc-400">{site.community_text}</p>
      </Reveal>

      {loading ? (
        <p className="mt-20 font-serif text-zinc-500" data-testid="mentoria-loading">Carregando…</p>
      ) : (
        <div className="mt-20 grid items-start gap-8 md:grid-cols-2 lg:grid-cols-3">
          {items.map((item, i) => (
            <MentoriaCard key={item.id} item={item} index={i} initialCount={counts[item.id] || 0} onOpen={open} />
          ))}
        </div>
      )}

      <Reveal className="mt-24 border border-gold/15 bg-[#0a0a0a] p-10 lg:p-14">
        <p className="text-xs font-semibold uppercase tracking-[0.25em] text-gold">Lembrete</p>
        <p className="mt-6 max-w-3xl font-serif text-2xl leading-relaxed text-zinc-100 lg:text-3xl">
          "Motivação te faz começar. Disciplina te faz continuar."
        </p>
      </Reveal>
    </div>
  );
}
